// DNA CONNECT - Portal das Famílias
// Layout principal com menu lateral

import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Home, Users, GraduationCap, CreditCard, MessageCircle, 
  Calendar, FileText, Car, User, LogOut, Menu, X, Bell,
  ChevronRight, Shield, AlertTriangle, ExternalLink, Sparkles, DollarSign, Ticket
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API_URL = window.location.origin;

// Menu items do DNA CONNECT
const menuItems = [
  { id: 'inicio', label: 'Início', icon: Home, path: '/familia' },
  { id: 'filhos', label: 'Meus Filhos', icon: Users, path: '/familia/filhos' },
  { id: 'pedagogico', label: 'Pedagógico', icon: GraduationCap, path: '/familia/pedagogico' },
  { id: 'estudos', label: 'DNA IA Estudos', icon: Sparkles, path: '/familia/estudos' },
  { id: 'aulas-extras', label: 'Aulas Extras 2026', icon: Sparkles, path: '/familia/aulas-extras' },
  { id: 'financeiro', label: 'DNA PAY', icon: DollarSign, path: '/familia/financeiro' },
  { id: 'comunicacao', label: 'Comunicação', icon: MessageCircle, path: '/familia/comunicacao' },
  { id: 'calendario', label: 'Calendário', icon: Calendar, path: '/familia/calendario' },
  { id: 'documentos', label: 'Documentos', icon: FileText, path: '/familia/documentos' },
  { id: 'eventos', label: 'Eventos', icon: Sparkles, path: '/familia/eventos' },
  { id: 'dnago', label: 'DNA GO', icon: Car, path: '/familia/dnago' },
  { id: 'perfil', label: 'Perfil e Segurança', icon: User, path: '/familia/perfil' },
];

export default function DnaConnectLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState(0);
  const [isImpersonation, setIsImpersonation] = useState(false);
  const [enabledModules, setEnabledModules] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Carregar módulos habilitados
  const loadEnabledModules = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/dnaconnect/dashboard?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        if (data.modulos_habilitados) {
          setEnabledModules(data.modulos_habilitados);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar módulos:', error);
    }
  };

  useEffect(() => {
    loadEnabledModules();
  }, []);

  // Filtrar menu items baseado nos módulos habilitados
  const filteredMenuItems = menuItems.filter(item => {
    // Início e Perfil sempre visíveis
    if (item.id === 'inicio' || item.id === 'perfil') return true;
    // Eventos: sempre visível (publicação automática vinda do V2 Eventos)
    if (item.id === 'eventos') return true;
    
    // Se ainda não carregou os módulos, não mostra nada (exceto início e perfil)
    if (!enabledModules) return false;
    
    // Só mostra se explicitamente habilitado (enabled === true)
    return enabledModules[item.id] === true;
  });

  useEffect(() => {
    // Verificar se é modo de impersonation
    const impersonateToken = searchParams.get('impersonate_token');
    const impersonateData = sessionStorage.getItem('dnaconnect_impersonate');
    
    if (impersonateToken || impersonateData) {
      try {
        const data = impersonateData ? JSON.parse(impersonateData) : null;
        
        if (data && data.isImpersonation) {
          setIsImpersonation(true);
          setUser(data.user);
          
          // Armazenar token para as requisições
          localStorage.setItem('familyToken', data.token);
          localStorage.setItem('familyUser', JSON.stringify(data.user));
          
          // Limpar parâmetro da URL sem recarregar
          if (impersonateToken) {
            window.history.replaceState({}, '', '/familia');
          }
          
          toast.info('Modo de visualização ativado', {
            description: `Visualizando como: ${data.user.responsible_name}`
          });
          return;
        }
      } catch (e) {
        console.error('Erro ao processar impersonation:', e);
      }
    }
    
    // Carregar dados do usuário normal
    const userData = localStorage.getItem('familyUser');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    
    // Verificar autenticação
    const token = localStorage.getItem('familyToken');
    if (!token) {
      navigate('/familia/login');
    }
  }, [navigate, searchParams]);

  const handleLogout = () => {
    localStorage.removeItem('familyToken');
    localStorage.removeItem('familyUser');
    sessionStorage.removeItem('dnaconnect_impersonate');
    toast.success('Logout realizado com sucesso');
    navigate('/familia/login');
  };

  const handleExitImpersonation = () => {
    localStorage.removeItem('familyToken');
    localStorage.removeItem('familyUser');
    sessionStorage.removeItem('dnaconnect_impersonate');
    toast.success('Modo de visualização encerrado');
    window.close(); // Fecha a aba
  };

  const isActive = (path) => {
    if (path === '/familia') {
      return location.pathname === '/familia';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner de Impersonation */}
      {isImpersonation && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-900 py-2 px-4 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium text-sm">
              Modo de Visualização: Você está vendo o portal como <strong>{user?.responsible_name}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="bg-white hover:bg-amber-100 text-amber-900 border-amber-600"
              onClick={handleExitImpersonation}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Sair do Modo Visualização
            </Button>
          </div>
        </div>
      )}

      {/* Header Mobile */}
      <header className={`lg:hidden fixed left-0 right-0 z-50 bg-gradient-to-r from-purple-700 to-purple-900 text-white shadow-lg ${isImpersonation ? 'top-10' : 'top-0'}`}>
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-white/10"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-2">
            <img 
              src="/novo-dna-logo.png" 
              alt="NOVO DNA" 
              className="h-8 w-auto object-contain"
            />
            <span className="font-bold text-lg">DNA CONNECT</span>
          </div>
          
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-white/10 relative">
              <Bell className="w-5 h-5" />
              {notifications > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                  {notifications}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar Overlay Mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed left-0 h-full w-72 bg-gradient-to-b from-purple-800 to-purple-900 text-white z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        ${isImpersonation ? 'top-10' : 'top-0'}
      `} style={{ height: isImpersonation ? 'calc(100% - 40px)' : '100%' }}>
        {/* Logo */}
        <div className="p-6 border-b border-purple-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/novo-dna-logo.png" 
                alt="NOVO DNA" 
                className="h-10 w-auto object-contain"
              />
              <div>
                <h1 className="font-bold text-xl">DNA CONNECT</h1>
                <p className="text-xs text-purple-300">Portal das Famílias</p>
              </div>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* User Info */}
        {user && (
          <div className="p-4 border-b border-purple-700/50">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isImpersonation ? 'bg-amber-500' : 'bg-purple-700'}`}>
                <span className="text-lg font-bold">
                  {user.responsible_name?.charAt(0) || 'F'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user.responsible_name}</p>
                <p className="text-xs text-purple-300 truncate">{user.email}</p>
                {isImpersonation && (
                  <Badge className="bg-amber-500 text-amber-900 mt-1 text-xs">
                    Visualização Admin
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Menu */}
        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                  ${active 
                    ? 'bg-white text-purple-900 shadow-lg' 
                    : 'hover:bg-white/10 text-purple-100'
                  }
                `}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-purple-600' : ''}`} />
                <span className="font-medium">{item.label}</span>
                {active && <ChevronRight className="w-4 h-4 ml-auto text-purple-600" />}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-purple-700/50">
          {isImpersonation ? (
            <button
              onClick={handleExitImpersonation}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-amber-200 bg-amber-600/20 hover:bg-amber-600/30 transition-all"
            >
              <ExternalLink className="w-5 h-5" />
              <span>Sair do Modo Visualização</span>
            </button>
          ) : (
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-purple-200 hover:bg-white/10 transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span>Sair</span>
            </button>
          )}
          
          <p className="text-xs text-purple-400 text-center mt-4">
            NOVO DNA © 2026
          </p>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`lg:ml-72 min-h-screen ${isImpersonation ? 'pt-26 lg:pt-10' : 'pt-16 lg:pt-0'}`}>
        {/* Desktop Header */}
        <header className={`hidden lg:flex items-center justify-between px-8 py-4 bg-white border-b shadow-sm ${isImpersonation ? '' : ''}`}>
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {menuItems.find(m => isActive(m.path))?.label || 'DNA CONNECT'}
            </h2>
          </div>
          
          <div className="flex items-center gap-4">
            <button className="p-2 rounded-lg hover:bg-gray-100 relative">
              <Bell className="w-5 h-5 text-gray-600" />
              {notifications > 0 && (
                <Badge className="absolute -top-1 -right-1 bg-red-500">{notifications}</Badge>
              )}
            </button>
            
            {user && (
              <div className={`flex items-center gap-3 px-4 py-2 rounded-xl ${isImpersonation ? 'bg-amber-50 border border-amber-200' : 'bg-purple-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isImpersonation ? 'bg-amber-500' : 'bg-purple-600'}`}>
                  <span className="text-sm font-bold text-white">
                    {user.responsible_name?.charAt(0) || 'F'}
                  </span>
                </div>
                <span className={`font-medium ${isImpersonation ? 'text-amber-900' : 'text-purple-900'}`}>
                  {user.responsible_name?.split(' ')[0]}
                </span>
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
